/**
 * VelocityTab — Tasks Reports Phase 1.
 *
 * Surfaces completion rate, turnaround, time-to-first-action, and
 * average time spent in each status. Time values arrive in minutes from the
 * backend and are formatted client-side into the friendliest unit.
 */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Hourglass, PlayCircle, Workflow, CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// Status palette — matches the donut on the Volume tab for visual consistency
const STATUS_COLORS = {
  New: '#3B82F6', 'In Progress': '#EC4899', Review: '#F59E0B',
  Revision: '#EF4444', Completed: '#10B981',
};

const fmtDuration = (mins) => {
  if (!mins || mins <= 0) return '—';
  if (mins < 60) return `${mins}m`;
  const hours = mins / 60;
  if (hours < 24) return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
  const days = hours / 24;
  return `${days.toFixed(days < 10 ? 1 : 0)}d`;
};

const buildParams = (filters) => {
  const p = { from_date: filters.fromDate, to_date: filters.toDate };
  if (filters.assigneeId && filters.assigneeId !== 'all') p.assignee_id = filters.assigneeId;
  if (filters.clientId && filters.clientId !== 'all') p.client_id = filters.clientId;
  if (filters.department && filters.department !== 'all') p.department = filters.department;
  if (filters.taskType && filters.taskType !== 'all') p.task_type = filters.taskType;
  if (filters.subtype && filters.subtype !== 'all') p.subtype = filters.subtype;
  if (filters.priority && filters.priority !== 'all') p.priority = filters.priority;
  if (filters.search) p.search = filters.search;
  return p;
};

const Tile = ({ icon: Icon, label, value, hint, tone = 'neutral', testid }) => {
  const toneCls = {
    pink: 'text-pink-700', emerald: 'text-emerald-700', amber: 'text-amber-700',
    red: 'text-red-700', blue: 'text-blue-700', neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>{value}</div>
      <div className="text-[11px] text-neutral-400 mt-2">{hint}</div>
    </div>
  );
};

const StatusBars = ({ minutesByStatus }) => {
  const entries = Object.entries(minutesByStatus || {});
  if (entries.length === 0) {
    return <div className="text-sm text-neutral-500 text-center py-10">No status timings yet</div>;
  }
  const max = Math.max(...entries.map(([, m]) => m), 1);
  // Sort by typical workflow order
  const order = ['New', 'In Progress', 'Review', 'Revision', 'Completed'];
  entries.sort(([a], [b]) => {
    const ai = order.indexOf(a); const bi = order.indexOf(b);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });
  return (
    <div className="space-y-2.5">
      {entries.map(([s, m]) => (
        <div key={s} className="flex items-center gap-3 text-[12px]">
          <div className="w-24 text-neutral-700 inline-flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: STATUS_COLORS[s] || '#737373' }} />
            {s}
          </div>
          <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${(m / max) * 100}%`, background: STATUS_COLORS[s] || '#737373' }}
            />
          </div>
          <div className="w-16 text-right tabular-nums text-neutral-600 font-medium">{fmtDuration(m)}</div>
        </div>
      ))}
    </div>
  );
};

const VelocityTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/admin/reports/tasks/velocity`, { headers: auth(), params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load velocity metrics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[110px]" />
        ))}
      </div>
    );
  }

  const t = data?.tiles || {};
  const tis = data?.avg_time_in_status_minutes || {};

  return (
    <div className="space-y-4" data-testid="velocity-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={CheckCircle2} label="Completion rate" value={`${t.completion_rate ?? 0}%`}
          hint={`${t.completed ?? 0} completed / ${t.assigned ?? 0} assigned`}
          tone={t.completion_rate >= 80 ? 'emerald' : t.completion_rate >= 50 ? 'amber' : 'red'}
          testid="velocity-tile-completion-rate"
        />
        <Tile
          icon={Hourglass} label="Avg turnaround" value={fmtDuration(t.avg_turnaround_minutes)}
          hint="created → completed"
          tone="blue"
          testid="velocity-tile-turnaround"
        />
        <Tile
          icon={PlayCircle} label="Time-to-first-action" value={fmtDuration(t.avg_ttfa_minutes)}
          hint="created → In Progress"
          tone="amber"
          testid="velocity-tile-ttfa"
        />
        <Tile
          icon={Zap} label="Throughput basis"
          value={t.completed ?? 0}
          hint={`completed in window`}
          tone="pink" testid="velocity-tile-completed"
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="velocity-status-bars">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[13px] font-semibold text-[#1a1a2e] flex items-center gap-2">
              <Workflow className="w-4 h-4 text-pink-600" />
              Avg time spent in each status
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              Across completed + in-flight tasks (open intervals counted up to now)
            </div>
          </div>
        </div>
        <StatusBars minutesByStatus={tis} />
      </div>
    </div>
  );
};

export default VelocityTab;
